/**
 * RampForm
 * Main single-screen form with modal overlays (new NowRamp design).
 * Replaces the old multi-step wizard.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { RampProvider, useRamp, type RampProviderProps } from '../context/RampContext';
import { CheckoutStep } from './CheckoutStep';
import { ProcessingStep } from './ProcessingStep';
import { CompleteStep } from './CompleteStep';
import { ErrorStep } from './ErrorStep';
import { ConfirmStep } from './ConfirmStep';
import { Modal } from './ui/Modal';
import { FormSkeleton } from './ui/Skeleton';
import { Toast } from './ui/Toast';
import { LoadingScreen } from './form/LoadingScreen';
import { BottomSheet } from './ui/BottomSheet';
import { FiatCurrencyModal } from './modals/FiatCurrencyModal';
import { CryptoCurrencyModal } from './modals/CryptoCurrencyModal';
import { PaymentMethodModal } from './modals/PaymentMethodModal';
import { SettingsScreen } from './modals/SettingsScreen';
import { CountryPickerModal } from './modals/CountryPickerModal';
import { LanguagePickerModal } from './modals/LanguagePickerModal';
import { COUNTRY_MAP } from '../data/countries';
import {
	SKINS,
	parseAmount,
	formatOutput,
	isValidHex,
	isStablecoin,
	MIN_FIAT_AMOUNT,
	MIN_CRYPTO_AMOUNT,
	getChainInfo,
	getChainIconUrl,
	type Skin,
} from '../data/cryptoData';
import type { Transaction } from '@nowramp/sdk';

// V2 sub-components
import { HeaderBar } from './form/HeaderBar';
import { SpendCard } from './form/SpendCard';
import { ReceiveCard } from './form/ReceiveCard';
import { PaymentCard } from './form/PaymentCard';
import { ProviderCard } from './form/ProviderCard';
import { ChooseRampModal } from './modals/ChooseRampModal';
import { WalletCard } from './form/WalletCard';
import { CtaButton } from './form/CtaButton';
import { Footer } from './form/Footer';
import { RateBreakdown } from './form/RateBreakdown';
import { getMethodIcon, getMethodDisplayName } from './PaymentMethodSelector';
import { useT, useI18n } from '../i18n';
import { I18nProvider, type LanguageCode } from '../i18n';

// =============================================================================
// Types
// =============================================================================

export interface RampFormProps extends Omit<RampProviderProps, 'children'> {
	className?: string;
	theme?: 'light' | 'dark';
	/** Skin preset ID */
	skinId?: string;
	/** Custom accent color override (hex) */
	accentColor?: string;
	/** Custom background color override (hex) */
	bgColor?: string;
	/** Custom background image URL */
	bgImage?: string;
	/** Custom border color (hex) */
	borderColor?: string;
	/** Custom border radius (CSS value, e.g. '5px') */
	borderRadius?: string;
	// Extended theme color overrides
	/** Surface/card background color (hex) */
	surfaceColor?: string;
	/** Input/pill background color (hex) */
	inputBgColor?: string;
	/** Primary text color (hex) */
	textColor?: string;
	/** Secondary/label text color (hex) */
	textSecondaryColor?: string;
	/** Muted text color (hex) */
	textMutedColor?: string;
	/** Success/rate indicator color (hex) */
	successColor?: string;
	/** Show Buy/Sell flow toggle (default: true). Set false to lock to the provided flowType. */
	showFlowToggle?: boolean;
	/** Show settings menu icon (default: true). Set false to hide settings entirely. */
	showSettings?: boolean;
	/** Show dark mode toggle in settings (default: true). Set false to hide theme switching. */
	showThemeToggle?: boolean;
	/** Auto-redirect for redirect checkouts */
	autoRedirect?: boolean;
	/** Block explorer URL pattern */
	explorerUrl?: string;
	/** Done button text */
	doneButtonText?: string;
	/** Support email */
	supportEmail?: string;
	/** Show email field in wallet step */
	showEmail?: boolean;
	/** Require email */
	requireEmail?: boolean;
	/** Show fee-free stablecoin amount (partner absorbs fees) */
	stablecoinParity?: boolean;
	/** Partner display name for parity info banner */
	partnerName?: string;
	/** Explicit container height in px. When set, post-form screens use this as fixed height. Form itself is content-driven with no min-height. */
	containerHeight?: number;
	/** Callback when user clicks done */
	onDone?: () => void;
	/** Language code for translations (default: 'en') */
	locale?: LanguageCode;
	onComplete?: (status: Transaction) => void;
	onError?: (error: Error) => void;
}

// =============================================================================
// Inner Form
// =============================================================================

interface InnerFormProps {
	className?: string;
	theme?: 'light' | 'dark';
	skinId?: string;
	accentColor?: string;
	bgColor?: string;
	bgImage?: string;
	borderColor?: string;
	borderRadius?: string;
	surfaceColor?: string;
	inputBgColor?: string;
	textColor?: string;
	textSecondaryColor?: string;
	textMutedColor?: string;
	successColor?: string;
	showFlowToggle?: boolean;
	showSettings?: boolean;
	showThemeToggle?: boolean;
	autoRedirect?: boolean;
	explorerUrl?: string;
	doneButtonText?: string;
	supportEmail?: string;
	stablecoinParity?: boolean;
	partnerName?: string;
	containerHeight?: number;
	locale?: LanguageCode;
	onDone?: () => void;
}

type ModalType = 'fiat' | 'crypto' | 'payment' | 'provider' | 'settings' | 'country' | 'language' | null;

function InnerForm({
	className = '',
	theme = 'dark',
	skinId: skinIdProp = 'mono',
	accentColor: accentColorProp,
	bgColor: bgColorProp,
	bgImage: bgImageProp,
	borderColor: borderColorProp,
	borderRadius: borderRadiusProp,
	surfaceColor: surfaceColorProp,
	inputBgColor: inputBgColorProp,
	textColor: textColorProp,
	textSecondaryColor: textSecondaryColorProp,
	textMutedColor: textMutedColorProp,
	successColor: successColorProp,
	showFlowToggle = true,
	showSettings = true,
	showThemeToggle = true,
	autoRedirect = false,
	explorerUrl,
	doneButtonText,
	supportEmail,
	stablecoinParity,
	partnerName,
	containerHeight,
	locale: _locale,
	onDone,
}: InnerFormProps) {
	void _locale; // consumed by I18nProvider wrapper
	const t = useT();
	const {
		state,
		dispatch,
		config,
		configLoading,
		quotes,
		quotesLoading,
		quotesError,
		fetchQuotes,
		createOrder,
		orderLoading,
		amountError,
		fieldLocks,
		apiConfig,
		assetBaseUrl,
		availableFiats,
		availableCryptos,
		availablePaymentMethods,
		selectionUnsupported,
		quotesEmpty,
		bestUnavailableReason,
	} = useRamp();

	const { locale: currentLocale, setLocale } = useI18n();

	// Local UI state
	const [darkMode, setDarkMode] = useState(theme === 'dark');
	const [skinId, setSkinId] = useState(skinIdProp);
	const [modal, setModal] = useState<ModalType>(null);
	const [amountTouched, setAmountTouched] = useState(false);
	const [walletTouched, setWalletTouched] = useState(false);
	const [walletFocused, setWalletFocused] = useState(false);
	const [walletValidating, setWalletValidating] = useState(false);
	const [walletServerValid, setWalletServerValid] = useState<boolean | null>(null);
	const [loading, setLoading] = useState(true);
	const [switching, setSwitching] = useState(false);
	const [autoSelectPending, setAutoSelectPending] = useState(false);
	const [manualProvider, setManualProvider] = useState(false);

	// V2 state
	const [toast, setToast] = useState<{ type: 'success' | 'error'; title: string; desc?: string } | null>(null);
	const [rateBreakdownOpen, setRateBreakdownOpen] = useState(false);
	const [rateCountdown, setRateCountdown] = useState(30);
	const [rateFlash, setRateFlash] = useState(false);
	const [amountShake, setAmountShake] = useState(false);


	const inputRef = useRef<HTMLInputElement>(null);
	const walletValidationTimer = useRef<ReturnType<typeof setTimeout>>();
	const prevTabRef = useRef(state.flowType);

	const postFormHeight = containerHeight ?? 640;

	// Reset manual provider override when new quotes arrive (inputs changed)
	const prevQuotesRef = useRef(quotes);
	useEffect(() => {
		if (quotes && quotes !== prevQuotesRef.current) {
			setManualProvider(false);
		}
		prevQuotesRef.current = quotes;
	}, [quotes]);

	// Clear stale quote when new quotes start loading (inputs changed)
	useEffect(() => {
		if (quotesLoading && !manualProvider) {
			dispatch({ type: 'CLEAR_QUOTE' });
		}
	}, [quotesLoading, manualProvider, dispatch]);

	// Auto-select best quote into state when quotes arrive (unless user manually picked a provider)
	useEffect(() => {
		if (manualProvider || autoSelectPending) return;
		if (quotes?.bestQuote) {
			dispatch({ type: 'SELECT_QUOTE', quote: quotes.bestQuote });
		}
	}, [quotes?.bestQuote, manualProvider, autoSelectPending, dispatch]);

	// Sync darkMode with theme prop
	useEffect(() => {
		setDarkMode(theme === 'dark');
	}, [theme]);

	// Initial load animation
	useEffect(() => {
		const t = setTimeout(() => setLoading(false), configLoading ? 0 : 800);
		return () => clearTimeout(t);
	}, [configLoading]);

	// Tab switching animation
	useEffect(() => {
		if (prevTabRef.current !== state.flowType && !loading) {
			prevTabRef.current = state.flowType;
			setSwitching(true);
			setAmountTouched(false);
			setWalletTouched(false);
			const t = setTimeout(() => setSwitching(false), 350);
			return () => clearTimeout(t);
		}
		prevTabRef.current = state.flowType;
	}, [state.flowType, loading]);

	const tab = state.flowType;
	const isBuy = tab === 'buy';

	// Compute display values from real quotes only
	const cryptoCode = state.cryptoCurrency || 'BTC';
	const fiatCode = state.fiatCurrency || 'USD';
	const spendAmount = isBuy ? state.fiatAmount : state.cryptoAmount;
	const spendNumeric = parseAmount(spendAmount);

	const activeQuote = state.selectedQuote || quotes?.bestQuote || null;

	const quoteRate = activeQuote?.exchangeRate
		? parseFloat(activeQuote.exchangeRate)
		: null;

	// Persist last known rate so it stays visible between quote fetches
	const lastRateRef = useRef<{ rate: number; crypto: string; fiat: string } | null>(null);
	if (quoteRate && cryptoCode && fiatCode) {
		lastRateRef.current = { rate: quoteRate, crypto: cryptoCode, fiat: fiatCode };
	}

	// Clear stale rate when no providers can quote or amount is zero
	useEffect(() => {
		if (quotesEmpty || quotesError) {
			lastRateRef.current = null;
		}
	}, [quotesEmpty, quotesError]);

	// Close rate breakdown and clear quote when spend amount becomes zero/empty
	useEffect(() => {
		if (!spendNumeric) {
			setRateBreakdownOpen(false);
			if (state.selectedQuote) {
				dispatch({ type: 'CLEAR_QUOTE' });
			}
		}
	}, [spendNumeric, state.selectedQuote, dispatch]);

	const receiveAmount = useMemo(() => {
		if (activeQuote) {
			const amt = isBuy ? activeQuote.cryptoAmount : activeQuote.fiatAmount;
			if (amt) return amt;
		}
		return '0';
	}, [activeQuote, isBuy]);

	// Rate flash when receive amount changes
	const prevReceiveRef = useRef(receiveAmount);
	useEffect(() => {
		if (receiveAmount !== prevReceiveRef.current && receiveAmount !== '0' && prevReceiveRef.current !== '0') {
			setRateFlash(true);
			const t = setTimeout(() => setRateFlash(false), 500);
			return () => clearTimeout(t);
		}
		prevReceiveRef.current = receiveAmount;
	}, [receiveAmount]);

	// Stablecoin parity: partner absorbs fees, credits user full amount
	// Same-peg (crypto code contains fiat code) → 1:1 fiat amount.
	// Cross-currency → FX-convert via quote rate.
	const parityActive = !!(stablecoinParity && partnerName && isBuy && isStablecoin(cryptoCode) && quoteRate);
	const isSamePeg = parityActive && cryptoCode.toUpperCase().includes(fiatCode.toUpperCase());
	const parityAmount = parityActive && spendNumeric > 0
		? formatOutput(isSamePeg ? spendNumeric : spendNumeric / quoteRate)
		: null;
	const displayReceiveAmount = parityActive && parityAmount ? parityAmount : receiveAmount;

	const displayRate = useMemo(() => {
		const r = quoteRate || lastRateRef.current?.rate;
		const cc = quoteRate ? cryptoCode : lastRateRef.current?.crypto;
		const fc = quoteRate ? fiatCode : lastRateRef.current?.fiat;
		if (!r || !cc || !fc) return '';
		return `1 ${cc} ≈ ${r.toFixed(2)} ${fc}`;
	}, [cryptoCode, fiatCode, quoteRate]);

	// Server-side wallet address validation (debounced with AbortController)
	useEffect(() => {
		const addr = state.walletAddress.trim();
		const network = state.network;

		// Reset when empty
		if (!addr || addr.length < 10) {
			setWalletServerValid(null);
			dispatch({ type: 'SET_WALLET_ERROR', message: '' });
			return;
		}

		setWalletValidating(true);
		setWalletServerValid(null);

		const abortController = new AbortController();

		clearTimeout(walletValidationTimer.current);
		walletValidationTimer.current = setTimeout(async () => {
			try {
				const res = await fetch(`${apiConfig.apiUrl}/public/validate/address`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ address: addr, ...(network ? { network } : {}) }),
					signal: abortController.signal,
				});
				const data = await res.json();
				const result = data.data?.attributes || data.data || data;
				if (result.isValid) {
					setWalletServerValid(true);
					dispatch({ type: 'SET_WALLET_ERROR', message: '' });
				} else {
					setWalletServerValid(false);
					const msg = result.errors?.[0] || result.details?.formatMessage || t('wallet.invalidServer');
					dispatch({ type: 'SET_WALLET_ERROR', message: msg });
				}
			} catch (err) {
				// Ignore aborted requests; network errors don't block the user
				if (err instanceof DOMException && err.name === 'AbortError') return;
				setWalletServerValid(null);
			} finally {
				if (!abortController.signal.aborted) {
					setWalletValidating(false);
				}
			}
		}, 500);

		return () => {
			clearTimeout(walletValidationTimer.current);
			abortController.abort();
		};
	}, [state.walletAddress, state.network, apiConfig.apiUrl]);

	// Validation — use server-provided limits (amountError) as source of truth, fall back to hardcoded
	const amountIsEmpty = !spendAmount || spendNumeric === 0;
	const amountBelowMin = spendNumeric > 0 && (isBuy ? spendNumeric < MIN_FIAT_AMOUNT : spendNumeric < MIN_CRYPTO_AMOUNT);
	const hasAmountError = !!amountError || amountBelowMin || (amountTouched && amountIsEmpty);
	const isAmountValid = spendNumeric > 0 && !amountError && !amountBelowMin;
	const isWalletValid = state.walletAddress.trim().length >= 10 && walletServerValid !== false && !walletValidating;
	const isFormValid = isAmountValid && (isBuy ? isWalletValid : true) && !!state.cryptoCurrency && !!state.network;

	const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let raw = e.target.value.replace(/[^0-9.,]/g, '');
		// Limit decimal places: 2 for fiat, 6 for crypto
		const maxDecimals = isBuy ? 2 : 6;
		const dotIndex = raw.indexOf('.');
		if (dotIndex !== -1 && raw.length - dotIndex - 1 > maxDecimals) {
			raw = raw.slice(0, dotIndex + maxDecimals + 1);
		}
		if (isBuy) {
			dispatch({ type: 'SET_FIAT_AMOUNT', amount: raw });
		} else {
			dispatch({ type: 'SET_CRYPTO_AMOUNT', amount: raw });
		}
	};

	const handleTabSwitch = (newTab: 'buy' | 'sell') => {
		if (newTab === tab) return;
		dispatch({ type: 'SET_FLOW_TYPE', flowType: newTab, fieldLocks });
	};

	const handleFiatSelect = useCallback(
		(code: string) => {
			dispatch({ type: 'SET_FIAT_CURRENCY', currency: code });
		},
		[dispatch]
	);

	const handleCryptoSelect = useCallback(
		(code: string, chain: string) => {
			dispatch({ type: 'SET_CRYPTO_CURRENCY', currency: code });
			dispatch({ type: 'SET_CRYPTO_CHAIN', chain });
		},
		[dispatch]
	);

	const handlePaymentMethodSelect = useCallback(
		(methodId: string) => {
			dispatch({ type: 'SET_PAYMENT_METHOD', methodId });
		},
		[dispatch]
	);

	const handleCountrySelect = useCallback(
		(code: string) => {
			dispatch({ type: 'SET_COUNTRY', country: code });
			setModal(null);
		},
		[dispatch]
	);

	const handleLocationPress = useCallback(() => {
		// Close settings, then open country picker after animation completes
		setModal(null);
		setTimeout(() => setModal('country'), 280);
	}, []);

	const handleLanguagePress = useCallback(() => {
		// Close settings, then open language picker after animation completes
		setModal(null);
		setTimeout(() => setModal('language'), 280);
	}, []);

	const countryName = state.country ? COUNTRY_MAP[state.country.toLowerCase()]?.name : undefined;

	const handleCtaClick = () => {
		if (!isFormValid) {
			if (hasAmountError || amountIsEmpty) {
				setAmountTouched(true);
				setAmountShake(true);
				setTimeout(() => setAmountShake(false), 400);
			}
			return;
		}
		// If a provider quote is already selected, go straight to order creation
		if (state.selectedQuote) {
			createOrder().catch((err) => {
				setToast({ type: 'error', title: t('toast.orderFailed'), desc: err instanceof Error ? err.message : t('toast.couldNotCreate') });
			});
			return;
		}
		setAutoSelectPending(true);
		fetchQuotes();
	};

	// Auto-select best quote when quotes arrive after CTA click
	useEffect(() => {
		if (!autoSelectPending || quotesLoading) return;

		if (quotes?.bestQuote) {
			setAutoSelectPending(false);
			dispatch({ type: 'SELECT_QUOTE', quote: quotes.bestQuote });
			createOrder().catch((err) => {
				setToast({ type: 'error', title: t('toast.orderFailed'), desc: err instanceof Error ? err.message : t('toast.couldNotCreate') });
			});
		} else if (quotesError) {
			setAutoSelectPending(false);
			const msg = quotesError.message || t('error.noProviders');
			dispatch({ type: 'SET_ERROR', message: msg });
			setToast({ type: 'error', title: t('toast.quoteError'), desc: msg });
		} else if (quotes && !quotes.bestQuote) {
			// Quotes loaded successfully but no providers available
			setAutoSelectPending(false);
			const reasons = (quotes as any).unavailableGateways
				?.map((g: any) => g.reason)
				.filter(Boolean);
			const message = reasons?.length
				? `${t('error.noProviders')}: ${reasons.join('; ')}`
				: t('error.noProviders');
			dispatch({ type: 'SET_ERROR', message });
			setToast({ type: 'error', title: t('toast.noProviders'), desc: message });
		}
	}, [autoSelectPending, quotes, quotesLoading, quotesError, dispatch, createOrder]);

	// ─── Toast on step transitions ───
	const prevStepRef = useRef(state.step);
	useEffect(() => {
		const prev = prevStepRef.current;
		prevStepRef.current = state.step;
		if (prev === state.step) return;
		if (state.step === 'checkout') {
			setToast({ type: 'success', title: t('toast.orderCreated'), desc: t('toast.redirecting') });
		} else if (state.step === 'error' && state.errorMessage) {
			setToast({ type: 'error', title: t('toast.somethingWrong'), desc: state.errorMessage });
		}
	}, [state.step, state.errorMessage]);

	// ─── Rate countdown orchestration ───
	const isFormStep = state.step === 'amount';

	// Force-close sheet on step change
	useEffect(() => {
		if (!isFormStep) setRateBreakdownOpen(false);
	}, [isFormStep]);

	// Timer lives in InnerForm — RateBreakdown is pure presentational
	useEffect(() => {
		if (!rateBreakdownOpen || !isFormStep) return;
		setRateCountdown(30);
		const id = setInterval(() => {
			setRateCountdown(prev => {
				if (prev <= 1) { fetchQuotes(); return 30; }
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(id);
	}, [rateBreakdownOpen, isFormStep, fetchQuotes]);

	// Reset countdown when fresh quotes arrive
	useEffect(() => {
		if (rateBreakdownOpen && isFormStep && !quotesLoading) setRateCountdown(30);
	}, [quotesLoading, rateBreakdownOpen, isFormStep]);

	// Theme computation
	const themeClass = darkMode ? 'theme-dark' : 'theme-light';
	const activeSkin = SKINS.find((s: Skin) => s.id === skinId) || SKINS[0];
	const isMono = activeSkin.accent === null;

	const hasCustomAccent = accentColorProp ? isValidHex(accentColorProp) : false;

	// Auto-detect best text color for CTA based on accent luminance
	const getCtaTextColor = (hex: string): string => {
		const c = hex.replace('#', '');
		const r = parseInt(c.substring(0, 2), 16);
		const g = parseInt(c.substring(2, 4), 16);
		const b = parseInt(c.substring(4, 6), 16);
		const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
		return luminance > 0.5 ? '#000000' : '#ffffff';
	};

	const accentStyle: Record<string, string> = hasCustomAccent
		? { '--t-accent': accentColorProp!, '--t-cta-bg': accentColorProp!, '--t-cta-text': getCtaTextColor(accentColorProp!) }
		: isMono
			? {
					'--t-accent': darkMode ? '#ffffff' : '#111111',
					'--t-cta-bg': darkMode ? '#ffffff' : '#111111',
					'--t-cta-text': darkMode ? '#000000' : '#ffffff',
				}
			: {
					'--t-accent': activeSkin.accent!,
					'--t-cta-bg': activeSkin.accent!,
					'--t-cta-text': getCtaTextColor(activeSkin.accent!),
				};

	// Extended theme overrides
	const themeOverrides: Record<string, string> = {};
	if (surfaceColorProp && isValidHex(surfaceColorProp)) {
		themeOverrides['--t-surface'] = surfaceColorProp;
		themeOverrides['--t-modal-bg'] = surfaceColorProp;
	}
	if (inputBgColorProp && isValidHex(inputBgColorProp)) {
		themeOverrides['--t-pill-bg'] = inputBgColorProp;
		themeOverrides['--t-close-bg'] = inputBgColorProp;
	}
	if (textColorProp && isValidHex(textColorProp)) {
		themeOverrides['--t-text'] = textColorProp;
	}
	if (textSecondaryColorProp && isValidHex(textSecondaryColorProp)) {
		themeOverrides['--t-text-secondary'] = textSecondaryColorProp;
	}
	if (textMutedColorProp && isValidHex(textMutedColorProp)) {
		themeOverrides['--t-text-muted'] = textMutedColorProp;
		themeOverrides['--t-text-tertiary'] = textMutedColorProp;
	}
	if (successColorProp && isValidHex(successColorProp)) {
		themeOverrides['--t-success'] = successColorProp;
	}

	const backgroundStyle: Record<string, string> = {};
	if (bgColorProp && isValidHex(bgColorProp)) {
		backgroundStyle.backgroundColor = bgColorProp;
	} else {
		backgroundStyle.backgroundColor = 'var(--t-bg)';
	}
	if (bgImageProp && (bgImageProp.startsWith('https://') || bgImageProp.startsWith('data:image/'))) {
		backgroundStyle.backgroundImage = `url(${bgImageProp})`;
		backgroundStyle.backgroundSize = 'cover';
		backgroundStyle.backgroundPosition = 'center';
	}

	const borderStyle: Record<string, string> = {};
	if (borderColorProp && isValidHex(borderColorProp)) {
		borderStyle.border = `1px solid ${borderColorProp}`;
	}
	if (borderRadiusProp) {
		borderStyle.borderRadius = borderRadiusProp;
	}

	// Chain info for display
	const chainCode = state.network;
	const chainInfo = chainCode ? getChainInfo(chainCode) : null;
	const chainDisplay = chainInfo?.shortName || chainInfo?.name || chainCode;

	// Amount error message — API errors show immediately, local validation waits for blur
	const amountErrorMessage = amountError
		? amountError
		: (amountTouched && (amountBelowMin || amountIsEmpty))
			? (amountIsEmpty
				? t('error.amountZero')
				: isBuy
					? t('error.minAmountFiat', { min: String(MIN_FIAT_AMOUNT), currency: fiatCode })
					: t('error.minAmountCrypto', { min: String(MIN_CRYPTO_AMOUNT), currency: cryptoCode }))
			: undefined;

	// Wallet error message
	const walletErrorMessage = state.walletError
		? state.walletError
		: (walletTouched && !isWalletValid && !walletValidating)
			? t('wallet.invalid')
			: undefined;

	// Selected payment method
	const filteredPaymentMethods = availablePaymentMethods.filter(m => {
		if (!m.supportedFiats.includes(state.fiatCurrency)) return false;
		if (state.selectedQuote && m.providers?.length) {
			return m.providers.includes(state.selectedQuote.gatewayId);
		}
		return true;
	});
	const selectedPaymentMethod = filteredPaymentMethods.find(m => m.id === state.paymentMethodId) || filteredPaymentMethods[0];

	// If we're in a post-form step, render inside the same themed container
	const postFormStep =
		state.step === 'checkout' ? <CheckoutStep autoRedirect={autoRedirect} /> :
		state.step === 'processing' ? <ProcessingStep /> :
		state.step === 'complete' ? <CompleteStep explorerUrl={explorerUrl} doneButtonText={doneButtonText} onDone={onDone} /> :
		state.step === 'error' ? <ErrorStep supportEmail={supportEmail} onDone={onDone} doneButtonText={doneButtonText} /> :
		state.step === 'confirm' ? <ConfirmStep stablecoinParity={parityActive} partnerName={partnerName} /> :
		null;

	if (postFormStep) {
		return (
			<div
				className={`${themeClass} relative w-full max-w-[390px] overflow-hidden flex flex-col select-none transition-colors duration-[400ms] ${className}`}
				style={{
					fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
					height: postFormHeight,
					borderRadius: 'var(--t-container-radius)',
					...backgroundStyle,
					...accentStyle,
					...themeOverrides,
					...borderStyle,
				}}
			>
				{postFormStep}
			</div>
		);
	}

	return (
		<div
			className={`${themeClass} relative w-full max-w-[390px] overflow-hidden flex flex-col select-none transition-colors duration-[400ms] ${className}`}
			style={{
				fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
				minHeight: 640,
				borderRadius: 'var(--t-container-radius)',
				...backgroundStyle,
				...accentStyle,
				...themeOverrides,
				...borderStyle,
			}}
		>
			{/* V2 Header */}
			<HeaderBar
				tab={tab}
				onTabSwitch={handleTabSwitch}
				showFlowToggle={showFlowToggle}
				showSettings={showSettings}
				onOpenSettings={() => setModal('settings')}
				darkMode={darkMode}
			/>

			{/* Form body */}
			{loading || switching || configLoading ? (
				<div className="pt-[88px] px-3 flex-1 flex flex-col">
					{configLoading ? <FormSkeleton /> : <LoadingScreen />}
				</div>
			) : (
				<div key={tab} className="flex-1 overflow-hidden pt-[88px] px-3 flex flex-col gap-1 tab-fade-in">
					<SpendCard
						amount={spendAmount}
						onAmountChange={handleAmountChange}
						currencyCode={isBuy ? fiatCode : cryptoCode}
						isBuy={isBuy}
						onOpenModal={() => setModal(isBuy ? 'fiat' : 'crypto')}
						assetBaseUrl={assetBaseUrl}
						error={amountErrorMessage}
						fieldLocks={fieldLocks}
						onBlur={() => setAmountTouched(true)}
						inputRef={inputRef}
						chainCode={!isBuy ? chainCode : undefined}
						chainDisplay={!isBuy ? chainDisplay : undefined}
						getChainIconUrl={!isBuy ? getChainIconUrl : undefined}
						shaking={amountShake}
					/>

					<ReceiveCard
						amount={displayReceiveAmount}
						currencyCode={isBuy ? cryptoCode : fiatCode}
						chainCode={isBuy ? chainCode : undefined}
						chainDisplay={isBuy ? chainDisplay : undefined}
						isBuy={isBuy}
						onOpenModal={() => setModal(isBuy ? 'crypto' : 'fiat')}
						displayRate={displayRate}
						assetBaseUrl={assetBaseUrl}
						quotesLoading={quotesLoading}
						onOpenRateBreakdown={() => { if (spendNumeric > 0) setRateBreakdownOpen(true); }}
						fieldLocks={fieldLocks}
						spendNumeric={spendNumeric}
						getChainIconUrl={isBuy ? getChainIconUrl : undefined}
						autoSelectPending={autoSelectPending}
						rateFlash={rateFlash}
					/>

					{/* No-quote inline warning */}
					{quotesEmpty && spendNumeric > 0 && (
						<div
							className="px-4 py-2.5 mt-1"
							style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius)', fontSize: 12, color: 'var(--t-error)', lineHeight: '16px' }}
						>
							{bestUnavailableReason || t('error.noProviders')}
						</div>
					)}

					{/* Parity info banner */}
					{parityActive && spendNumeric > 0 && quotes?.bestQuote && (
						<div
							className="flex items-start gap-2 mt-1 px-4 py-2.5"
							style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius)', fontSize: 12, color: 'var(--t-text-secondary)', lineHeight: '16px' }}
						>
							<svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
								<circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
								<path d="M8 7v4M8 5.5v-.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
							</svg>
							<span>
								{t('parity.info', { receiveAmount, crypto: cryptoCode, spendAmount: formatOutput(spendNumeric), fiat: fiatCode, partner: partnerName || '' })}
							</span>
						</div>
					)}

					{/* Payment method */}
					{(availablePaymentMethods.length > 0 || (config?.paymentMethods && config.paymentMethods.length > 0)) && (
						filteredPaymentMethods.length > 0 ? (
							<PaymentCard
								selectedMethod={selectedPaymentMethod}
								onOpenModal={() => setModal('payment')}
								darkMode={darkMode}
								getMethodIcon={getMethodIcon}
								getMethodDisplayName={(id, name) => getMethodDisplayName(id, name, t, true)}
							/>
						) : (
							<div
								className="flex items-center justify-center px-4 mt-1"
								style={{
									background: 'var(--t-surface)',
									borderRadius: 'var(--t-card-radius)',
									height: 56,
								}}
							>
								<span className="text-[12px]" style={{ color: 'var(--t-text-muted)' }}>
									{t('payment.noneAvailable')}
								</span>
							</div>
						)
					)}

					{/* Provider selector */}
					{(quotes || quotesLoading) && (
						<ProviderCard
							selectedQuote={state.selectedQuote}
							quotesLoading={quotesLoading}
							onOpenModal={() => setModal('provider')}
							assetBaseUrl={assetBaseUrl}
						/>
					)}

					{/* Wallet address — only for buy (offramp collects via provider checkout) */}
					{isBuy && (
						<WalletCard
							address={state.walletAddress}
							onChange={(v) => dispatch({ type: 'SET_WALLET_ADDRESS', address: v })}
							placeholder={t('wallet.placeholderBuy')}
							isBuy={isBuy}
							error={walletErrorMessage}
							validating={walletValidating}
							disabled={!!fieldLocks?.destinationAddress?.locked}
							onFocus={() => setWalletFocused(true)}
							onBlur={() => { setWalletFocused(false); setWalletTouched(true); }}
							focused={walletFocused}
						/>
					)}

					<div className="flex-1" style={{ minHeight: 16 }} />

					{/* Unsupported selection message */}
					{selectionUnsupported && (
						<div className="text-[12px] text-center mb-2" style={{ color: 'var(--t-error)' }}>
							{isBuy ? t('error.currencyUnavailableBuy') : t('error.currencyUnavailableSell')}
						</div>
					)}

					{/* CTA + Footer */}
					<div className="flex flex-col gap-3 pb-3">
						<CtaButton
							label={isBuy
								? t('cta.buy', { crypto: cryptoCode })
								: t('cta.sell', { crypto: cryptoCode })}
							disabled={!isFormValid || orderLoading || autoSelectPending || selectionUnsupported || quotesEmpty}
							loading={orderLoading || autoSelectPending}
							onClick={handleCtaClick}
						/>
						<Footer />
					</div>
				</div>
			)}

			{/* Toast */}
			{toast && (
				<Toast
					type={toast.type}
					title={toast.title}
					description={toast.desc}
					visible={!!toast}
					onDismiss={() => setToast(null)}
				/>
			)}

			{/* Rate breakdown bottom sheet */}
			<BottomSheet isOpen={rateBreakdownOpen} onClose={() => setRateBreakdownOpen(false)}>
				<RateBreakdown
					quote={activeQuote}
					isOpen={rateBreakdownOpen}
					onClose={() => setRateBreakdownOpen(false)}
					countdown={rateCountdown}
					quotesLoading={quotesLoading}
				/>
			</BottomSheet>

			{/* Modal overlays */}
			<Modal isOpen={modal === 'fiat'} onClose={() => setModal(null)}>
				{(close) => (
					<FiatCurrencyModal
						onClose={close}
						onSelect={(v) => {
							handleFiatSelect(v);
							close();
						}}
						currencies={availableFiats.map((f) => ({ code: f.code, name: f.code }))}
						assetBaseUrl={assetBaseUrl}
						selectedCode={state.fiatCurrency}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'crypto'} onClose={() => setModal(null)}>
				{(close) => (
					<CryptoCurrencyModal
						onClose={close}
						onSelect={(code, chain) => {
							handleCryptoSelect(code, chain);
							close();
						}}
						cryptos={availableCryptos.map((c) => ({
							code: c.code,
							name: c.code,
							networks: c.networks?.map((n) => ({ code: n.id, name: n.name })),
						}))}
						assetBaseUrl={assetBaseUrl}
						selectedCode={state.cryptoCurrency}
						selectedChain={state.network}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'payment'} onClose={() => setModal(null)}>
				{(close) => (
					<PaymentMethodModal
						methods={filteredPaymentMethods}
						selectedId={state.paymentMethodId}
						onSelect={(id) => {
							handlePaymentMethodSelect(id);
							close();
						}}
						onClose={close}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'provider'} onClose={() => setModal(null)}>
				{(close) => (
					<ChooseRampModal
						quotes={quotes}
						quotesLoading={quotesLoading}
						cryptoCurrency={cryptoCode}
						selectedGatewayId={state.selectedProvider}
						assetBaseUrl={assetBaseUrl}
						onSelect={(quote) => {
							setManualProvider(true);
							dispatch({ type: 'SELECT_QUOTE', quote });
							close();
						}}
						onClose={close}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'settings'} onClose={() => setModal(null)} variant="right">
				{(close) => (
					<SettingsScreen
						onClose={close}
						darkMode={darkMode}
						onToggleDarkMode={setDarkMode}
						skinId={skinId}
						onSelectSkin={(id) => {
							setSkinId(id);
						}}
						showThemeToggle={showThemeToggle}
						country={state.country}
						countryName={countryName}
						onLocationPress={handleLocationPress}
						onLanguagePress={handleLanguagePress}
						assetBaseUrl={assetBaseUrl}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'country'} onClose={() => setModal(null)}>
				{(close) => (
					<CountryPickerModal
						onClose={close}
						onSelect={(code) => {
							handleCountrySelect(code);
							close();
						}}
						assetBaseUrl={assetBaseUrl}
						selectedCode={state.country}
					/>
				)}
			</Modal>
			<Modal isOpen={modal === 'language'} onClose={() => setModal(null)}>
				{(close) => (
					<LanguagePickerModal
						onClose={close}
						onSelect={(code) => {
							setLocale(code);
							close();
						}}
						selectedCode={currentLocale}
					/>
				)}
			</Modal>
		</div>
	);
}

// =============================================================================
// Main Component
// =============================================================================

export function RampForm({
	projectId,
	apiUrl,
	assetBaseUrl,
	flowType,
	customerId,
	brandId,
	defaultFiatCurrency,
	defaultCryptoCurrency,
	defaultNetwork,
	defaultFiatAmount,
	defaultCryptoAmount,
	defaultWalletAddress,
	defaultGateway,
	defaultCountry,
	fieldLocks,
	partnerMetadata,
	statusPollInterval,
	onComplete,
	onError,
	className,
	theme,
	skinId,
	accentColor,
	bgColor,
	bgImage,
	borderColor,
	borderRadius,
	surfaceColor,
	inputBgColor,
	textColor,
	textSecondaryColor,
	textMutedColor,
	successColor,
	showFlowToggle,
	showSettings,
	showThemeToggle,
	autoRedirect,
	explorerUrl,
	doneButtonText,
	supportEmail,
	stablecoinParity,
	partnerName,
	containerHeight,
	locale,
	onDone,
}: RampFormProps) {
	return (
		<RampProvider
			projectId={projectId}
			apiUrl={apiUrl}
			assetBaseUrl={assetBaseUrl}
			flowType={flowType}
			customerId={customerId}
			brandId={brandId}
			defaultFiatCurrency={defaultFiatCurrency}
			defaultCryptoCurrency={defaultCryptoCurrency}
			defaultNetwork={defaultNetwork}
			defaultFiatAmount={defaultFiatAmount}
			defaultCryptoAmount={defaultCryptoAmount}
			defaultWalletAddress={defaultWalletAddress}
			defaultGateway={defaultGateway}
			defaultCountry={defaultCountry}
			fieldLocks={fieldLocks}
			partnerMetadata={partnerMetadata}
			statusPollInterval={statusPollInterval}
			onComplete={onComplete}
			onError={onError}
		>
			<I18nProvider defaultLocale={locale || 'en'}>
				<InnerForm
					className={className}
					theme={theme}
					skinId={skinId}
					accentColor={accentColor}
					bgColor={bgColor}
					bgImage={bgImage}
					borderColor={borderColor}
					borderRadius={borderRadius}
					surfaceColor={surfaceColor}
					inputBgColor={inputBgColor}
					textColor={textColor}
					textSecondaryColor={textSecondaryColor}
					textMutedColor={textMutedColor}
					successColor={successColor}
					showFlowToggle={showFlowToggle}
					showSettings={showSettings}
					showThemeToggle={showThemeToggle}
					autoRedirect={autoRedirect}
					explorerUrl={explorerUrl}
					doneButtonText={doneButtonText}
					supportEmail={supportEmail}
					stablecoinParity={stablecoinParity}
					partnerName={partnerName}
					containerHeight={containerHeight}
					onDone={onDone}
				/>
			</I18nProvider>
		</RampProvider>
	);
}
